import React, { useState } from "react";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Plus, Trash2, UserPlus } from "lucide-react";

/**
 * Batch 3 · Fase 3 — Additional Contacts editor.
 *
 * Beheert extra contactpersonen binnen HETZELFDE klant-record. Geen aparte
 * accounts. Velden per contact: name (verplicht), email, phone, role.
 *
 * Props:
 *  - value: Array<{id?, name, email?, phone?, role?}>
 *  - onChange: (nieuweLijst) => void
 *  - disabled: bool (readonly voor rollen zonder edit_contact)
 *  - testidPrefix: string voor data-testid ids
 */
export default function AdditionalContactsBlock({ value, onChange, disabled, testidPrefix = "klant-additional-contact" }) {
  const list = Array.isArray(value) ? value : [];

  const genLocalId = () => `local-${Math.random().toString(36).slice(2, 10)}`;

  const patchAt = (index, patch) => {
    const next = list.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange?.(next);
  };
  const addRow = () => {
    onChange?.([...list, { id: genLocalId(), name: "", email: "", phone: "", role: "" }]);
  };
  const removeAt = (index) => {
    onChange?.(list.filter((_, i) => i !== index));
  };

  return (
    <div data-testid={`${testidPrefix}s-block`} className="mt-4 border border-gray-200 bg-[#F8F9FA] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-[#0055FF]" />
          <h4 className="font-chivo font-black text-xs uppercase tracking-wider text-[#0A192F]">
            Extra contactpersonen ({list.length})
          </h4>
        </div>
        {!disabled && (
          <Button
            type="button"
            onClick={addRow}
            data-testid={`${testidPrefix}s-add-btn`}
            className="bg-white text-[#050505] border border-gray-300 hover:bg-gray-50 rounded-none font-chivo font-bold uppercase text-[10px] px-2 py-1 h-auto"
          >
            <Plus className="w-3 h-3 mr-1" /> Contact toevoegen
          </Button>
        )}
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-gray-500 italic" data-testid={`${testidPrefix}s-empty`}>
          Geen extra contactpersonen. Voeg optioneel personen zoals boekhouder, inkoper of magazijn toe.
        </p>
      ) : (
        <div className="space-y-2">
          {list.map((c, idx) => {
            const stableId = c.id || `idx-${idx}`;
            return (
              <div
                key={stableId}
                data-testid={`${testidPrefix}-row-${idx}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end bg-white border border-gray-200 p-3"
              >
                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wide font-bold text-gray-600">Naam *</Label>
                  <Input
                    data-testid={`${testidPrefix}-${idx}-name`}
                    value={c.name || ""}
                    onChange={(e) => patchAt(idx, { name: e.target.value })}
                    disabled={disabled}
                    placeholder="Voor- en achternaam"
                    className="rounded-none mt-1"
                  />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wide font-bold text-gray-600">E-mail</Label>
                  <Input
                    data-testid={`${testidPrefix}-${idx}-email`}
                    type="email"
                    value={c.email || ""}
                    onChange={(e) => patchAt(idx, { email: e.target.value })}
                    disabled={disabled}
                    placeholder="naam@bedrijf.nl"
                    className="rounded-none mt-1"
                  />
                </div>
                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-wide font-bold text-gray-600">Telefoon</Label>
                  <Input
                    data-testid={`${testidPrefix}-${idx}-phone`}
                    type="tel"
                    value={c.phone || ""}
                    onChange={(e) => patchAt(idx, { phone: e.target.value })}
                    disabled={disabled}
                    placeholder="+31 6 12 34 56 78"
                    className="rounded-none mt-1"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[10px] uppercase tracking-wide font-bold text-gray-600">Functie / Rol</Label>
                  <Input
                    data-testid={`${testidPrefix}-${idx}-role`}
                    value={c.role || ""}
                    onChange={(e) => patchAt(idx, { role: e.target.value })}
                    disabled={disabled}
                    placeholder="Bv. Boekhouder"
                    className="rounded-none mt-1"
                  />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      data-testid={`${testidPrefix}-${idx}-remove`}
                      title="Contact verwijderen"
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 border border-transparent hover:border-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {disabled && (
        <div className="text-[10px] text-gray-500 mt-2">Alleen lezen — je hebt geen rechten om contactpersonen te wijzigen.</div>
      )}
    </div>
  );
}
